import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import aws_config from '../../modified_aws-export';
import { MultiValue } from 'react-select';
import { FetchOptionType, IOptionType, partPositionDatabaseNamespaceValue } from "../../types/common";
import { getOnlyMatchToOEM } from "../../services/matchedPairs/getOnlyMatchToOEM";
import { PageBody, PageHeader, PageLayout } from '../../components';
import { ManualDrawer, MatchedPairsFilter } from '../../features';
import { ImageItemResult } from "../../features/ImageItemResult/ImageItemResult";
import { useMatchToOEM } from "../../hooks/useMatchToOEM";
import { useLoadOptions } from '../../hooks/useLoadOptions';
import './MatchedPairs.scss';
import { useConfigState, useExtractedImagesState } from '../../hooks';

const MatchedPairsPage: React.FC = () => {
    const { t } = useTranslation();
    const {
        manufacturerOptions,
    } = useLoadOptions([FetchOptionType.Manufacturer]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [noItems, setNoItems] = useState<number>(10);
    const [displayResults, setDisplayResult] = useState<any[]>([]);
    const [selectedManufacturerOptions, setSelectedManufacturerOptions] = useState<IOptionType[]>([]);
    const { unmatchOemClick } = useMatchToOEM();
    const { databaseNamespaces } = useConfigState();
    const [selectedNamespace, setSelectedNamespace] = useState<IOptionType>(databaseNamespaces);
    const { setDisplayImageManuals, selectedImages, displayImageManuals } = useExtractedImagesState();
    const [checked, setChecked] = useState<boolean[]>(new Array(displayResults.length).fill(false));

    const fetchOnlyMatchToOEM = useCallback(async () => {
        setIsLoading(true);
        try {
            const manufacturers = selectedManufacturerOptions.map(manufacturer => ({ Manufacturer: manufacturer.value }));
            const filters = [{ namespace: "OEM" }, { matchedBy: "ImageMatcherTool" }, ...manufacturers];
            const namespace = selectedNamespace.value === "BTL" ? partPositionDatabaseNamespaceValue : null;

            const result = await getOnlyMatchToOEM(aws_config.aws_user_files_s3_bucket, noItems, filters, namespace);
            setDisplayResult(result);
            setChecked(new Array(result.length).fill(false));
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedManufacturerOptions, noItems, selectedNamespace]);

    useEffect(() => {
        fetchOnlyMatchToOEM();
    }, [fetchOnlyMatchToOEM]);

    useEffect(() => {
        const isDisplayManualsDetail = selectedImages.length > 0;
        setDisplayImageManuals(isDisplayManualsDetail);
        if (!isDisplayManualsDetail) {
            setChecked(new Array(displayResults.length).fill(false))
        }
    }, [selectedImages, setDisplayImageManuals, displayImageManuals, displayResults]);


    const handleSelectManufacturers = (values: MultiValue<IOptionType>) => {
        setSelectedManufacturerOptions(values.map(item => ({ value: item.value, label: item.label })));
    };

    const handleApplyFilter = async (event: React.FormEvent) => {
        event.preventDefault();
        await fetchOnlyMatchToOEM();
    };

    const onchangeNamespace = (value: IOptionType) => {
        setSelectedNamespace(value);
    }

    const renderMatchedPairsResult = () => {
        return (
            <div className="row p-3">
                {displayResults.length > 0 ? (
                    displayResults.map((elem: any, index) => (
                        <div className="col justify-content-center d-flex" key={elem.id}>
                            <ImageItemResult
                                index={index}
                                metadata={elem.metadata}
                                elements={elem.elements}
                                onUnmatchClick={() => unmatchOemClick(elem.metadata, selectedNamespace.value === "BTL" ? "BtlAll" : "AllImages")}
                                checked={checked}
                                setChecked={setChecked}
                            />
                        </div>
                    ))
                ) : (
                    <div className="alert alert-warning justify-content-center d-flex" role="alert">
                        {t("Filter.resultsNotFound")}
                    </div>
                )}
            </div>
        )
    }

    return (
        <PageLayout>
            <PageHeader title={t("OnlyMatchToOEM.MatchesPairs")} isDisplayBack={false} t={t}
                right={<MatchedPairsFilter
                    manufacturers={manufacturerOptions}
                    selectedManufacturerOptions={selectedManufacturerOptions}
                    selectedNamespace={selectedNamespace}
                    onchangeNamespace={onchangeNamespace}
                    noItems={noItems}
                    onChangeManufacturers={handleSelectManufacturers}
                    onChangeNoItems={setNoItems}
                    handleApplyFilter={handleApplyFilter}
                    isLoading={isLoading}
                    t={t}
                />}
            />

            <PageBody isLoading={isLoading} t={t}>
                <div className="matched-pairs-container">
                    {renderMatchedPairsResult()}
                </div>
                {<ManualDrawer forceFeedback={false} />}
            </PageBody>
        </PageLayout>
    );
};

export default MatchedPairsPage;
